import {
  Box,
  Button,
  HStack,
  Heading,
  Icon,
  SimpleGrid,
  Text,
} from "@chakra-ui/react"
import type React from "react"
import {
  FaArrowLeft,
  FaArrowRight,
  FaBolt,
  FaCheckCircle,
  FaStar,
  FaTrophy,
} from "react-icons/fa"

export type SessionCompleteSecondaryAction = {
  label: string
  onClick: () => void
}

export function SessionCompleteSummary({
  heading = "Session complete!",
  description = "Strong finish. Your review progress and XP have been saved.",
  cardsCompleted,
  xpEarned,
  onBack,
  secondaryAction,
}: {
  heading?: string
  description?: string
  cardsCompleted: number
  xpEarned: number
  onBack: () => void
  secondaryAction?: SessionCompleteSecondaryAction
}) {
  return (
    <Box
      data-testid="session-complete-summary"
      position="relative"
      overflow="hidden"
      w="full"
      maxW="720px"
      mx="auto"
      borderWidth={1}
      borderColor="teal.200"
      borderRadius="2xl"
      bg="white"
      _dark={{ bg: "gray.900", borderColor: "teal.700" }}
      px={{ base: 5, md: 8 }}
      py={{ base: 7, md: 9 }}
      boxShadow="0 24px 70px rgba(15, 118, 110, 0.16)"
    >
      <Box
        position="absolute"
        top="-78px"
        right="-68px"
        boxSize="180px"
        borderRadius="full"
        bg="teal.50"
        _dark={{ bg: "teal.950" }}
      />
      <Box
        position="absolute"
        bottom="-86px"
        left="-72px"
        boxSize="170px"
        borderRadius="full"
        bg="yellow.50"
        _dark={{ bg: "yellow.950" }}
      />

      <Box position="relative" textAlign="center">
        <Box
          mx="auto"
          mb={5}
          boxSize="96px"
          borderRadius="full"
          bg="teal.500"
          color="white"
          display="grid"
          placeItems="center"
          boxShadow="0 16px 34px rgba(13, 148, 136, 0.28)"
        >
          <Icon as={FaTrophy} boxSize={11} />
        </Box>

        <HStack justify="center" gap={2} mb={3} color="yellow.500">
          <Icon as={FaStar} />
          <Icon as={FaStar} />
          <Icon as={FaStar} />
        </HStack>

        <Text
          fontSize="xs"
          textTransform="uppercase"
          letterSpacing="wider"
          color="teal.600"
          _dark={{ color: "teal.300" }}
          fontWeight="bold"
          mb={2}
        >
          Study streak secured
        </Text>
        <Heading size={{ base: "lg", md: "xl" }} mb={3}>
          {heading}
        </Heading>
        <Text color="gray.600" _dark={{ color: "gray.300" }} mb={6}>
          {description}
        </Text>

        <SimpleGrid columns={{ base: 1, sm: 2 }} gap={3} mb={7}>
          <SessionStat
            icon={FaBolt}
            label="XP earned"
            value={`+${xpEarned}`}
            color="yellow.500"
          />
          <SessionStat
            icon={FaCheckCircle}
            label="Cards completed"
            value={String(cardsCompleted)}
            color="teal.500"
          />
        </SimpleGrid>

        <HStack justify="center" gap={3} wrap="wrap">
          <Button size="lg" onClick={onBack}>
            <HStack gap={2}>
              <Icon as={FaArrowLeft} />
              <Text>Back to Study Hub</Text>
            </HStack>
          </Button>
          {secondaryAction && (
            <Button
              size="lg"
              colorPalette="teal"
              onClick={secondaryAction.onClick}
            >
              <HStack gap={2}>
                <Text>{secondaryAction.label}</Text>
                <Icon as={FaArrowRight} />
              </HStack>
            </Button>
          )}
        </HStack>
      </Box>
    </Box>
  )
}

function SessionStat({
  icon,
  label,
  value,
  color,
}: {
  icon: React.ElementType
  label: string
  value: string
  color: string
}) {
  return (
    <Box
      borderWidth={1}
      borderColor="gray.200"
      _dark={{ borderColor: "gray.700", bg: "gray.800" }}
      borderRadius="lg"
      bg="gray.50"
      p={4}
      textAlign="left"
    >
      <HStack gap={3}>
        <Box
          boxSize="38px"
          borderRadius="full"
          bg="white"
          _dark={{ bg: "gray.950" }}
          display="grid"
          placeItems="center"
          color={color}
          boxShadow="sm"
          flexShrink={0}
        >
          <Icon as={icon} />
        </Box>
        <Box>
          <Text fontSize="2xl" fontWeight="bold" lineHeight={1}>
            {value}
          </Text>
          <Text fontSize="xs" color="gray.500" fontWeight="semibold">
            {label}
          </Text>
        </Box>
      </HStack>
    </Box>
  )
}
